import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";

import type { Notification } from "../types";

/**
 * Linked entity kinds shown beside the tile icon for alert notifications.
 * Resolved only from FK ids — never from `alert_tag` or description text.
 */
export type CategoryEntitySourceKind = "task" | "resource" | "client";

export type ResolvedCategoryEntity = {
  kind: CategoryEntitySourceKind;
  /** Display name when the API sent one; otherwise the kind word. */
  name: string;
  /** Kind word for the eyebrow (Task / Resource / Client). */
  kindLabel: string;
  /** Font Awesome class matching the sidebar nav icon for this kind. */
  iconClass: string;
};

const KIND_LABELS: Record<CategoryEntitySourceKind, string> = {
  task: "Task",
  resource: "Resource",
  client: "Client",
};

const KIND_ICONS: Record<CategoryEntitySourceKind, string> = {
  task: SWIVU_STANDARD_ICONS[VIEW_TYPES.tasks] as string,
  resource: SWIVU_STANDARD_ICONS[VIEW_TYPES.resources] as string,
  client: SWIVU_STANDARD_ICONS[VIEW_TYPES.clients] as string,
};

function pickTrimmed(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed || null;
}

/**
 * Alert notifications: which entity triggered this update.
 * - `task` id set → Task
 * - else `resource` id set → Resource
 * - else `client` id set → Client
 *
 * Priority matches the order above when more than one id is present.
 * Ignores `alert_tag` and description quotes.
 */
export function resolveCategoryEntity(
  notification: Notification,
  _fallbackCategoryKey?: string,
): ResolvedCategoryEntity | null {
  if (notification.task != null) {
    return {
      kind: "task",
      name: pickTrimmed(notification.task_name) ?? KIND_LABELS.task,
      kindLabel: KIND_LABELS.task,
      iconClass: KIND_ICONS.task,
    };
  }
  if (notification.resource != null) {
    return {
      kind: "resource",
      name: pickTrimmed(notification.resource_name) ?? KIND_LABELS.resource,
      kindLabel: KIND_LABELS.resource,
      iconClass: KIND_ICONS.resource,
    };
  }
  if (notification.client != null) {
    return {
      kind: "client",
      name: pickTrimmed(notification.client_name) ?? KIND_LABELS.client,
      kindLabel: KIND_LABELS.client,
      iconClass: KIND_ICONS.client,
    };
  }
  return null;
}

/** @deprecated Prefer `kindLabel` on the resolved entity; kept for callers. */
export function formatCategoryEntityCue(
  entity: ResolvedCategoryEntity,
): string {
  return entity.kindLabel;
}

/**
 * Legacy per-category map — entity kind is now FK-only for all alerts.
 * Kept so imports/docs that referenced the dictionary still resolve.
 */
export type CategoryEntityDisplayConfig = {
  prefer: CategoryEntitySourceKind[];
  showKindLabel: boolean;
};

export const CATEGORY_ENTITY_DISPLAY: Record<string, CategoryEntityDisplayConfig> =
  {
    schedule: { prefer: ["task", "resource", "client"], showKindLabel: true },
    status: { prefer: ["task", "resource", "client"], showKindLabel: true },
    reminders: { prefer: ["task", "resource", "client"], showKindLabel: true },
    nudges: { prefer: ["task", "resource", "client"], showKindLabel: true },
    notes: { prefer: ["task", "resource", "client"], showKindLabel: true },
    board_alert: { prefer: ["task", "resource", "client"], showKindLabel: true },
    general: { prefer: ["task", "resource", "client"], showKindLabel: true },
    file: { prefer: ["task", "resource", "client"], showKindLabel: true },
    leads: { prefer: ["task", "resource", "client"], showKindLabel: true },
    marketing: { prefer: ["task", "resource", "client"], showKindLabel: true },
    incentive: { prefer: ["task", "resource", "client"], showKindLabel: true },
    import: { prefer: ["task", "resource", "client"], showKindLabel: true },
    context: { prefer: ["task", "resource", "client"], showKindLabel: true },
    copy: { prefer: ["task", "resource", "client"], showKindLabel: true },
  };
